import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Snackbar, TextField, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  askPublicMotiveWebReportChat,
  getPublicMotiveWebReport,
  getPublicMotiveWebReportChatHistory,
  resetPublicMotiveWebReportChatHistory,
} from '../../../API/surveys';
import { createMockMotiveReportData } from './motive-web-report.mock';
import { ChatMessage, MotiveReportData } from './motive-web-report.types';

interface Props {
  token: string;
}

const initialAssistantMessage =
  'I can explain your result in simple words. Ask me what matters most, what to improve first, or how to communicate your profile to your manager.';

const MotiveWebReport: React.FC<Props> = ({ token }) => {
  const { classes } = useStyles();
  const [chatInput, setChatInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [chatLoading, setChatLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [reportData, setReportData] = useState<MotiveReportData | null>(null);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    const loadReport = async () => {
      setLoading(true);
      setError('');

      const [result, historyResult] = await Promise.all([
        getPublicMotiveWebReport(token),
        getPublicMotiveWebReportChatHistory(token),
      ]);
      if ('error' in result) {
        // Keep the page usable even when API is temporarily unavailable.
        setReportData(createMockMotiveReportData(token));
        setError('Could not load live report data. Showing fallback demo data.');
      } else {
        setReportData(result.data);
      }

      if (!('error' in historyResult) && historyResult.data.messages.length > 0) {
        setMessages(historyResult.data.messages.map((m) => ({ role: m.role, content: m.content, createdAt: m.createdAt })));
      } else {
        setMessages([{ role: 'assistant', content: initialAssistantMessage, createdAt: new Date().toISOString() }]);
      }
      setLoading(false);
    };

    loadReport();
  }, [token]);

  const quickPrompts = useMemo(
    () => [
      'Summarize my top 3 takeaways in 60 seconds.',
      'What should I improve first this week?',
      'Explain my story detections in simple words.',
    ],
    []
  );

  const formatMessageTime = (timestamp?: string) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleAsk = async (prompt?: string) => {
    const question = (prompt || chatInput).trim();
    if (!question || chatLoading) return;

    const userMessage: ChatMessage = { role: 'user', content: question, createdAt: new Date().toISOString() };
    setMessages((prev) => [...prev, userMessage]);
    setChatInput('');
    setChatLoading(true);

    const response = await askPublicMotiveWebReportChat(token, question);
    if ('error' in response) {
      const fallbackMessage: ChatMessage = {
        role: 'assistant',
        content: 'I could not reach the report chat service right now. Please try again in a moment.',
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, fallbackMessage]);
    } else {
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response.data.assistantMessage?.content || response.data.answer,
        createdAt: response.data.assistantMessage?.createdAt || new Date().toISOString(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
    }

    setChatLoading(false);
  };

  const handleResetConversation = async () => {
    const result = await resetPublicMotiveWebReportChatHistory(token);
    if ('error' in result) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'I could not reset the conversation right now. Please try again.',
          createdAt: new Date().toISOString(),
        },
      ]);
      setToast({
        open: true,
        message: 'Could not reset conversation. Please try again.',
        severity: 'error',
      });
      return;
    }
    setMessages([{ role: 'assistant', content: initialAssistantMessage, createdAt: new Date().toISOString() }]);
    setToast({
      open: true,
      message: 'Conversation cleared.',
      severity: 'success',
    });
    setConfirmResetOpen(false);
  };

  if (loading || !reportData) {
    return (
      <Box className={classes.page}>
        <Box className={classes.loadingState}>
          <Typography variant="h6">Loading report...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.page}>
      <Box className={classes.layout}>
        <Box className={classes.resultsColumn}>
          {error && <Alert severity="warning">{error}</Alert>}
          <Paper className={classes.hero} elevation={0}>
            <Typography variant="h4" className={classes.title}>
              MOTiVE Web Report
            </Typography>
            <Typography className={classes.subtitle}>
              Easy to read, action-focused, and supported by chat guidance.
            </Typography>
            <Box className={classes.metaRow}>
              <Chip label={`Participant: ${reportData.participantName}`} size="small" />
              <Chip label={`Type: ${reportData.reportType}`} size="small" />
              <Chip label={`Language: ${reportData.language.toUpperCase()}`} size="small" />
            </Box>
          </Paper>

          <Paper className={classes.section} elevation={0}>
            <Typography variant="h5" className={classes.sectionTitle}>
              Key Takeaways
            </Typography>
            <Typography className={classes.sectionHint}>
              Short insights first. Each card tells you what it means and what to do next.
            </Typography>
            <Box className={classes.takeawayGrid}>
              {reportData.takeaways.map((takeaway) => (
                <Paper key={takeaway.title} className={classes.card} elevation={0}>
                  <Typography className={classes.cardTitle}>{takeaway.title}</Typography>
                  <Typography className={classes.score}>{takeaway.score}/100</Typography>
                  <Box className={classes.progressTrack}>
                    <Box className={classes.progressFill} style={{ width: `${takeaway.score}%` }} />
                  </Box>
                  <Typography className={classes.cardText}>{takeaway.description}</Typography>
                  <Alert severity="success" className={classes.actionAlert}>
                    {takeaway.action}
                  </Alert>
                </Paper>
              ))}
            </Box>
          </Paper>

          <Paper className={classes.section} elevation={0}>
            <Typography variant="h5" className={classes.sectionTitle}>
              Picture Stories and AI Detections
            </Typography>
            <Typography className={classes.sectionHint}>
              This section shows where the model found motive signals and how confident it is.
            </Typography>
            <Box className={classes.storyList}>
              {reportData.stories.map((story) => (
                <Paper key={story.id} className={classes.storyCard} elevation={0}>
                  <Box className={classes.storyImage}>{story.imageLabel}</Box>
                  <Box className={classes.storyBody}>
                    <Typography className={classes.cardTitle}>{story.title}</Typography>
                    <Typography className={classes.cardText}>{story.text}</Typography>
                    <Box className={classes.tagsRow}>
                      {story.tags.map((tag) => (
                        <Chip key={tag} label={tag} size="small" />
                      ))}
                    </Box>
                    <Box className={classes.detectionList}>
                      {story.detections.map((detection) => (
                        <Box key={`${story.id}-${detection.motive}`} className={classes.detectionRow}>
                          <Typography className={classes.detectionLabel}>{detection.motive}</Typography>
                          <Box className={classes.progressTrack}>
                            <Box className={classes.progressFill} style={{ width: `${detection.confidence}%` }} />
                          </Box>
                          <Typography className={classes.detectionValue}>{detection.confidence}%</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Paper>

          <Paper className={classes.section} elevation={0}>
            <Typography variant="h5" className={classes.sectionTitle}>
              Motive Profile and Next Steps
            </Typography>
            <Box className={classes.scoreGrid}>
              {reportData.motiveScores.map((score) => (
                <Paper key={score.motive} className={classes.card} elevation={0}>
                  <Typography className={classes.cardTitle}>{score.motive}</Typography>
                  <Typography className={classes.score}>{score.score}/100</Typography>
                  <Box className={classes.progressTrack}>
                    <Box className={classes.progressFill} style={{ width: `${score.score}%`, backgroundColor: score.color }} />
                  </Box>
                  <Typography className={classes.cardText}>{score.description}</Typography>
                </Paper>
              ))}
            </Box>
            <Box className={classes.nextStepList}>
              {reportData.nextSteps.map((step) => (
                <Alert key={step} severity="info" className={classes.nextStepAlert}>
                  {step}
                </Alert>
              ))}
            </Box>
          </Paper>
        </Box>

        <Box className={classes.chatColumn}>
          <Paper className={classes.chatWrapper} elevation={0}>
            <Box className={classes.chatHeader}>
              <Box className={classes.chatTitleRow}>
                <SmartToyIcon fontSize="small" />
                <Typography variant="h6">Report Chat Assistant</Typography>
                <Button
                  size="small"
                  variant="text"
                  className={classes.resetButton}
                  onClick={() => setConfirmResetOpen(true)}
                  startIcon={<RestartAltIcon fontSize="small" />}
                >
                  Reset
                </Button>
              </Box>
              <Typography className={classes.chatHint}>Ask instead of reading everything yourself.</Typography>
            </Box>

            <Box className={classes.promptRow}>
              {quickPrompts.map((prompt) => (
                <Button key={prompt} variant="outlined" size="small" onClick={() => handleAsk(prompt)} className={classes.promptButton}>
                  {prompt}
                </Button>
              ))}
            </Box>

            <Box className={classes.messageList}>
              {messages.map((message, index) => (
                <Box key={`${message.role}-${index}`} className={message.role === 'assistant' ? classes.assistantBubble : classes.userBubble}>
                  <Typography className={classes.messageText}>{message.content}</Typography>
                  <Typography className={classes.messageMeta}>{formatMessageTime(message.createdAt)}</Typography>
                </Box>
              ))}
              {chatLoading && (
                <Box className={classes.typingBubble}>
                  <Typography className={classes.messageText}>Assistant is typing...</Typography>
                </Box>
              )}
            </Box>

            <Box className={classes.chatInputRow}>
              <TextField
                placeholder="Ask: What should I do first?"
                value={chatInput}
                onChange={(event) => setChatInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleAsk();
                  }
                }}
                fullWidth
                size="small"
                disabled={chatLoading}
              />
              <Button variant="contained" onClick={() => handleAsk()} className={classes.sendButton} disabled={chatLoading}>
                <SendIcon fontSize="small" />
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>

      <Dialog open={confirmResetOpen} onClose={() => setConfirmResetOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset conversation?</DialogTitle>
        <DialogContent>
          <Typography className={classes.confirmText}>
            This deletes all messages for this report link. You cannot undo this action.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setConfirmResetOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleResetConversation}>
            Reset now
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  page: {
    minHeight: 'calc(100vh - 64px)',
    padding: theme.spacing(2),
    background: 'linear-gradient(160deg, #eef2ff 0%, #f8fbff 100%)',
  },
  loadingState: {
    minHeight: 'calc(100vh - 64px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  layout: {
    width: '100%',
    maxWidth: 1400,
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 7fr) minmax(320px, 3fr)',
    gap: theme.spacing(2),
    alignItems: 'start',
    [theme.breakpoints.down('lg')]: {
      gridTemplateColumns: '1fr',
    },
  },
  resultsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  hero: {
    borderRadius: 16,
    border: '1px solid #d9e2ff',
    background: 'linear-gradient(135deg, #f7f9ff 0%, #edf3ff 100%)',
    padding: theme.spacing(2.5),
  },
  title: {
    fontWeight: 700,
  },
  subtitle: {
    marginTop: theme.spacing(0.5),
    color: '#485267',
  },
  metaRow: {
    marginTop: theme.spacing(1.5),
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  section: {
    borderRadius: 16,
    border: '1px solid #dfe5f2',
    backgroundColor: '#fff',
    padding: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 700,
  },
  sectionHint: {
    marginTop: theme.spacing(0.5),
    color: '#5c667d',
    marginBottom: theme.spacing(1.5),
  },
  takeawayGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: theme.spacing(1.5),
  },
  scoreGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: theme.spacing(1.5),
  },
  card: {
    borderRadius: 12,
    border: '1px solid #e6ebf5',
    backgroundColor: '#fafcff',
    padding: theme.spacing(1.5),
  },
  cardTitle: {
    fontWeight: 700,
    color: '#1f2a40',
  },
  score: {
    marginTop: theme.spacing(0.5),
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#3950d0',
  },
  progressTrack: {
    width: '100%',
    height: 8,
    borderRadius: 999,
    backgroundColor: '#e2e9ff',
    overflow: 'hidden',
    marginTop: theme.spacing(1),
  },
  progressFill: {
    height: '100%',
    borderRadius: 999,
    background: 'linear-gradient(90deg, #6d7df7 0%, #42d0b2 100%)',
  },
  cardText: {
    marginTop: theme.spacing(1),
    color: '#4c576e',
    fontSize: '0.92rem',
  },
  actionAlert: {
    marginTop: theme.spacing(1.2),
  },
  storyList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  storyCard: {
    borderRadius: 12,
    border: '1px solid #e6ebf5',
    display: 'grid',
    gridTemplateColumns: '130px minmax(0, 1fr)',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  storyImage: {
    minHeight: 130,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #6f81ff 0%, #4fd6b9 100%)',
  },
  storyBody: {
    padding: theme.spacing(1.5),
  },
  tagsRow: {
    marginTop: theme.spacing(1),
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.8),
  },
  detectionList: {
    marginTop: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.8),
  },
  detectionRow: {
    display: 'grid',
    gridTemplateColumns: '95px minmax(0, 1fr) 42px',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  detectionLabel: {
    fontSize: '0.85rem',
    color: '#47526a',
  },
  detectionValue: {
    fontSize: '0.85rem',
    fontWeight: 700,
    textAlign: 'right',
    color: '#273247',
  },
  nextStepList: {
    marginTop: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  nextStepAlert: {
    borderRadius: 10,
  },
  chatColumn: {
    position: 'sticky',
    top: theme.spacing(2),
    [theme.breakpoints.down('lg')]: {
      position: 'static',
    },
  },
  chatWrapper: {
    borderRadius: 16,
    border: '1px solid #dfe5f2',
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 640,
    maxHeight: 'calc(100vh - 32px)',
    overflow: 'hidden',
  },
  chatHeader: {
    padding: theme.spacing(1.5),
    borderBottom: '1px solid #ecf0f7',
  },
  chatTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  resetButton: {
    marginLeft: 'auto',
    textTransform: 'none',
  },
  chatHint: {
    marginTop: theme.spacing(0.6),
    color: '#4f5b74',
    fontSize: '0.9rem',
  },
  promptRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.8),
    padding: theme.spacing(1.2),
    borderBottom: '1px solid #ecf0f7',
  },
  promptButton: {
    justifyContent: 'flex-start',
    textAlign: 'left',
    textTransform: 'none',
  },
  messageList: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(1.2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    backgroundColor: '#fbfcff',
  },
  assistantBubble: {
    maxWidth: '92%',
    alignSelf: 'flex-start',
    backgroundColor: '#eef1ff',
    border: '1px solid #d8e0ff',
    borderRadius: 12,
    padding: theme.spacing(1.1),
    whiteSpace: 'pre-wrap',
  },
  typingBubble: {
    maxWidth: '92%',
    alignSelf: 'flex-start',
    backgroundColor: '#f2f4ff',
    border: '1px dashed #d8e0ff',
    borderRadius: 12,
    padding: theme.spacing(1.1),
  },
  userBubble: {
    maxWidth: '92%',
    alignSelf: 'flex-end',
    backgroundColor: '#e8fbf4',
    border: '1px solid #c6f0e1',
    borderRadius: 12,
    padding: theme.spacing(1.1),
    whiteSpace: 'pre-wrap',
  },
  messageText: {
    fontSize: '0.9rem',
    color: '#223049',
  },
  messageMeta: {
    marginTop: theme.spacing(0.4),
    fontSize: '0.72rem',
    color: '#70809f',
    textAlign: 'right',
  },
  chatInputRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gap: theme.spacing(1),
    padding: theme.spacing(1.2),
    borderTop: '1px solid #ecf0f7',
  },
  sendButton: {
    minWidth: 44,
  },
  confirmText: {
    marginTop: theme.spacing(0.5),
    color: '#4f5b74',
  },
}));

export default MotiveWebReport;
